import {Inject, Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {LoadWicketUser, SetWicketUser} from '@store/user/user.actions';
import {catchError, Observable, tap, throwError} from 'rxjs';
import {ClearTokens, FetchToken, InitAuth, Login, Logout, SetToken} from './auth.actions';
import {
  isRedirectedFromWicketLogin,
  isRedirectedFromWicketLogout,
} from '@tisa-group/core/utils';
import {Location} from '@angular/common';
import {LOCAL_STORAGE} from '@ng-web-apis/common';
import {WicketTokenData} from '@tisa-group/models';
import {WICKET_TOKEN_DATA_KEY} from '@core/constants/localstorage-keys';
import {AuthService} from '@core/services/auth.service';
import {HttpErrorResponse} from '@angular/common/http';
import {decodeToken} from '@tisa-group/cdk';
import {JwtPayload} from 'jwt-decode';
import {GoogleTagService} from '@core/services/google-tag.service';

export type AuthStateModel =
  | {
      token: string | null;
      refreshToken: string | null;
      isLoggedIn: boolean;
    }
  | undefined;

@State<AuthStateModel | undefined>({
  name: 'auth',
  defaults: undefined,
})
@Injectable()
export class AuthState {
  @Action(InitAuth)
  initAuth(ctx: StateContext<AuthStateModel>, {wicketEnabled}: InitAuth) {
    if (!wicketEnabled) {
      ctx.setState({
        token: null,
        refreshToken: null,
        isLoggedIn: false,
      });

      return;
    }

    const currentUrl = this.location.path();

    if (isRedirectedFromWicketLogin(currentUrl)) {
      this.authService.fetchTokenAfterWicketLogin();

      return;
    }

    if (isRedirectedFromWicketLogout(currentUrl)) {
      this.authService.logoutAfterWicketLogout();

      return;
    }

    // Synchronize tokens from local storage with auth store on app init if user is logged in
    ctx.dispatch(
      new SetToken(
        this.authService.tokenFromStorage,
        this.authService.refreshTokenFromStorage,
      ),
    );
  }

  @Action(Login)
  login(): void {
    return this.authService.login();
  }

  @Action(Logout)
  logout(): void {
    return this.authService.logout();
  }

  @Action(ClearTokens)
  clearTokens(ctx: StateContext<AuthStateModel>): void {
    this.store.dispatch(new SetWicketUser(null));

    this.authService.removeTokensFromStorage();
    this.googleTagService.resetUserId();

    ctx.setState({
      token: null,
      refreshToken: null,
      isLoggedIn: false,
    });
  }

  @Action(SetToken)
  setToken(
    ctx: StateContext<AuthStateModel>,
    {token, refreshToken, afterRefresh}: SetToken,
  ): void {
    ctx.setState({
      token,
      refreshToken,
      isLoggedIn: !!token,
    });

    if (token && refreshToken) {
      this.authService.saveTokensToLocalStorage(token as string, refreshToken as string);
      const userId = (decodeToken(token) as any)['custom:user_id'];

      if (userId) {
        this.googleTagService.pushUserId(userId);
      }

      if (!afterRefresh) {
        this.store.dispatch(LoadWicketUser);
      }
    } else {
      this.store.dispatch(new SetWicketUser(null));
      this.googleTagService.resetUserId();
    }
  }

  @Action(FetchToken)
  fetchToken(
    ctx: StateContext<AuthStateModel>,
    {code}: FetchToken,
  ): Observable<WicketTokenData> {
    ctx.setState(undefined);

    return this.authService.fetchToken(code).pipe(
      tap(tokenResponse => {
        const tokenExists = !!tokenResponse;

        if (tokenExists) {
          this.authService.saveTokenDataToLocalStorage(JSON.stringify(tokenResponse));

          const token = this.authService.tokenData?.id_token || null;
          const refreshToken = this.authService.tokenData?.refresh_token || null;

          ctx.dispatch(new SetToken(token, refreshToken));

          // Remove token data from local storage
          this.localStorage.removeItem(WICKET_TOKEN_DATA_KEY);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        ctx.dispatch(ClearTokens);

        return throwError(() => error);
      }),
    );
  }

  @Selector()
  static isLoadingToken(state: AuthStateModel): boolean {
    return state?.token === undefined;
  }

  @Selector()
  static token(state: AuthStateModel): string | null | undefined {
    return state?.token;
  }

  @Selector()
  static decodedToken(
    state: AuthStateModel,
  ): (JwtPayload & {'custom:user_id': string}) | null {
    return state?.token ? (decodeToken(state?.token) as any) : state?.token;
  }

  @Selector()
  static refreshToken(state: AuthStateModel): string | null | undefined {
    return state?.refreshToken;
  }

  @Selector()
  static isLoggedIn(state: AuthStateModel): boolean {
    return !!state?.token;
  }

  constructor(
    readonly authService: AuthService,
    readonly store: Store,
    readonly location: Location,
    readonly googleTagService: GoogleTagService,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
  ) {}
}
